import get from 'lodash.get';
import orderBy from 'lodash.orderby';

// Global Types
import { AppState } from 'services/types';

// Selectors
import { selectEntityTypeCostCentreDepth } from 'services/organizationConfig/selectors';

// Global Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import formatArrayAsList from 'utils/format-array-as-list';

// Local Types
import { EntityTypeId, EntityTypesState, EntityTypesProps } from './types';

const selectEntityTypes = (state: AppState): EntityTypesState =>
    state.entityTypes;
const selectEntityTypeId = (
    _state: AppState,
    props: EntityTypesProps
): EntityTypeId => props.entityTypeId;

const selectEntityTypesArray = createDeepEqualSelector(
    selectEntityTypes,
    (entityTypesState) => Object.values(entityTypesState)
);

const selectEntityTypesExist = createDeepEqualSelector(
    selectEntityTypesArray,
    (entityTypesArrayState) => !!entityTypesArrayState.length
);

const selectEntityTypesSize = createDeepEqualSelector(
    selectEntityTypesArray,
    (entityTypesArrayState) => entityTypesArrayState.length
);

const selectEntityType = createDeepEqualSelector(
    [selectEntityTypes, selectEntityTypeId],
    (entityTypesState, entityTypeId) => get(entityTypesState, entityTypeId)
);

// TODO: refactor out generic selector
const makeSelectEntityTypeValue = (field: string): any =>
    createDeepEqualSelector(selectEntityType, (entityTypeState) =>
        get(entityTypeState, field)
    );

const selectEntityTypeDepth = createDeepEqualSelector(
    selectEntityType,
    (entityType) => get(entityType, 'depth')
);

const makeSelectEntityTypeDepth = (): typeof selectEntityTypeDepth =>
    selectEntityTypeDepth;

const selectEntityTypeName = createDeepEqualSelector(
    selectEntityType,
    (entityType) => get(entityType, 'name')
);

const makeSelectEntityTypeName = (): typeof selectEntityTypeName =>
    selectEntityTypeName;

const selectEntityTypePluralName = createDeepEqualSelector(
    selectEntityType,
    (entityType) => get(entityType, 'pluralName')
);

const makeSelectEntityTypePluralName = (): typeof selectEntityTypePluralName =>
    selectEntityTypePluralName;

const selectEntityTypeFirstDepth = createDeepEqualSelector(
    selectEntityTypesArray,
    (entityTypesArrayState) => {
        if (!entityTypesArrayState.length) return undefined;
        return entityTypesArrayState.reduce((accumulator, currentValue) => {
            const depthA = accumulator.depth;
            const depthB = currentValue.depth;
            return depthA < depthB ? accumulator : currentValue;
        });
    }
);

// TODO: refactor out generic selector
const makeSelectEntityTypeFirstDepthValue = (field: string): any =>
    createDeepEqualSelector(
        selectEntityTypeFirstDepth,
        (entityTypeFirstDepthState) => get(entityTypeFirstDepthState, field)
    );

const selectIsFirstDepth = createDeepEqualSelector(
    selectEntityTypeDepth,
    (entityTypeDepthState) => entityTypeDepthState === 1
);

const makeSelectIsFirstDepth = (): typeof selectIsFirstDepth =>
    selectIsFirstDepth;

const selectIsLastDepth = createDeepEqualSelector(
    [selectEntityTypeDepth, selectEntityTypesSize],
    (entityTypeDepthState, entityTypesSizeState) =>
        entityTypeDepthState === entityTypesSizeState
);

const makeSelectIsLastDepth = (): typeof selectIsLastDepth => selectIsLastDepth;

const selectIsCostCentreAllowed = createDeepEqualSelector(
    [selectEntityTypeDepth, selectEntityTypeCostCentreDepth],
    (entityTypeDepth, costCentreDepthConfig): boolean =>
        !!(costCentreDepthConfig && costCentreDepthConfig >= entityTypeDepth)
);

const makeSelectIsCostCentreAllowed = (): typeof selectIsCostCentreAllowed =>
    selectIsCostCentreAllowed;

const selectNextEntityTypeValue = createDeepEqualSelector(
    [selectEntityTypesArray, selectEntityTypeDepth],
    (entityTypesArrayState, entityTypeDepthState) =>
        entityTypesArrayState &&
        entityTypesArrayState.find(
            (value) => value.depth === entityTypeDepthState + 1
        )
);

// TODO: refactor out generic selector
const makeSelectNextEntityTypeValue = (field: string): any =>
    createDeepEqualSelector(
        selectNextEntityTypeValue,
        (nextEntityTypeValueState) => get(nextEntityTypeValueState, field)
    );

const selectPreviousEntityTypeValue = createDeepEqualSelector(
    [selectEntityTypesArray, selectEntityTypeDepth],
    (entityTypesArrayState, entityTypeDepthState) =>
        entityTypesArrayState &&
        entityTypesArrayState.find(
            (value) => value.depth === entityTypeDepthState - 1
        )
);
// TODO: refactor out generic selector
const makeSelectPreviousEntityTypeValue = (field: string): any =>
    createDeepEqualSelector(
        selectPreviousEntityTypeValue,
        (previousEntityTypeValueState) =>
            get(previousEntityTypeValueState, field)
    );

const selectEntityTypesOrderByDepth = createDeepEqualSelector(
    selectEntityTypesArray,
    (entityTypesArray) => orderBy(entityTypesArray, 'depth')
);

const selectChildEntityTypes = createDeepEqualSelector(
    [selectEntityTypesOrderByDepth, selectEntityTypeDepth],
    (entityTypesOrderByDepth, entityTypeDepth) => {
        const begin = entityTypeDepth;
        const end = entityTypesOrderByDepth.length;
        return entityTypesOrderByDepth.slice(begin, end);
    }
);

const selectChildEntityTypePluralNames = createDeepEqualSelector(
    selectChildEntityTypes,
    (childEntityTypes) => {
        const result = childEntityTypes.map(
            (value) => value.pluralName || value.name
        );
        return formatArrayAsList(result);
    }
);

const makeSelectChildEntityTypePluralNames = (): typeof selectChildEntityTypePluralNames =>
    selectChildEntityTypePluralNames;

export {
    selectEntityTypes,
    selectEntityTypesArray,
    selectEntityTypesExist,
    selectEntityTypeId,
    selectEntityType,
    makeSelectEntityTypeValue,
    selectEntityTypeDepth,
    selectEntityTypeName,
    makeSelectEntityTypeName,
    selectEntityTypePluralName,
    makeSelectEntityTypePluralName,
    makeSelectEntityTypeDepth,
    selectEntityTypeFirstDepth,
    makeSelectEntityTypeFirstDepthValue,
    makeSelectIsFirstDepth,
    makeSelectIsLastDepth,
    makeSelectIsCostCentreAllowed,
    makeSelectNextEntityTypeValue,
    makeSelectPreviousEntityTypeValue,
    makeSelectChildEntityTypePluralNames
};
